<template>
  <nav class="StudentBottomNav" :aria-label="$t('studentBottomNav.ariaLabel')">
    <router-link
      v-for="item in items"
      :key="item.to"
      class="StudentBottomNav-item"
      active-class="StudentBottomNav-item--active"
      :to="`/${$i18n.locale}/${item.to}`"
    >
      <component :is="item.icon" class="StudentBottomNav-icon" />
      <span class="StudentBottomNav-label">{{ $t(item.labelKey) }}</span>
    </router-link>
    <!--
      Borna bug batch 1 (item 8): V2's student bottom nav ends with its
      `.nav-item--logout` cell, after نمایه. It is a button, not a
      `router-link`: tapping it raises the SAME shared confirmation the
      sidebar row raises, and the shell root runs the one `AuthService.logout`
      on confirm. No second logout implementation exists.
    -->
    <button
      type="button"
      class="StudentBottomNav-item StudentBottomNav-item--logout"
      data-testid="student-bottom-nav-logout"
      :aria-label="$t('logoutConfirm.trigger')"
      @click="requestLogout"
    >
      <student-home-icon
        name="logout"
        :size="20"
        class="StudentBottomNav-icon"
      />
      <span class="StudentBottomNav-label">{{
        $t('appSidebar.sidebarLinks.studentLogout')
      }}</span>
    </button>
  </nav>
</template>

<script>
import HouseIcon from '@/assets/sidebar_icons/house.svg'
import ArchiveIcon from '@/assets/archive.svg'
import PortraitIcon from '@/assets/sidebar_icons/portrait.svg'
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import studentLogout from '@/utils/student-logout'

// P1: the student mobile bottom nav has exactly three real destinations --
// no اعتبار (credit), اعلان‌ها (notifications), or AI Tutor entry. Labels
// reuse the same i18n keys as the desktop sidebar (SidebarLinks.vue) so the
// two navigations never drift out of sync.
//
// Borna bug batch 1 (item 8): plus V2's trailing Logout cell. Ordering is
// V2's own -- خانه, گزارش‌ها, نمایه, خروج -- with V2's forbidden product
// surfaces (اعتبار / notifications, 00_*.md PF-11.1/PF-11.2) still absent, so
// this bar carries four cells where V2's demo carries six.
export default {
  name: 'student-bottom-nav',
  components: { StudentHomeIcon },
  mixins: [studentLogout],
  data() {
    return {
      items: [
        {
          to: 'dashboard',
          labelKey: 'appSidebar.sidebarLinks.studentHome',
          icon: HouseIcon,
        },
        {
          to: 'sessions/history',
          labelKey: 'appSidebar.sidebarLinks.learningHistory',
          icon: ArchiveIcon,
        },
        {
          to: 'profile',
          labelKey: 'appSidebar.sidebarLinks.studentProfile',
          icon: PortraitIcon,
        },
      ],
    }
  },
}
</script>

<style lang="scss" scoped>
// WP-04V-D (D4). Item composition is unchanged -- three items, per PF-11 #4.
// The SHAPE is corrected to the rendered prototype, which is a FLOATING bar
// inset 10px on three sides with a 19px radius, not the edge-to-edge strip
// with a top border this shipped as. Height is now declared here, once
// ($borna-bottom-nav-height), and every consumer derives its floor clearance
// from the same token instead of guessing (root cause R8).
// P40 QA-47: steps aside while the Student types in the chat composer
// (Composer.vue sets the class on <html>), so the composer can sit on the
// keyboard or the screen's edge.
html.borna-composer-typing .StudentBottomNav {
  display: none;
}

.StudentBottomNav {
  position: fixed;
  bottom: calc(#{$borna-bottom-nav-inset} + env(safe-area-inset-bottom));
  left: $borna-bottom-nav-inset;
  right: $borna-bottom-nav-inset;
  height: $borna-bottom-nav-height;
  z-index: 40;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(15px);
  border: 1px solid $borna-border;
  border-radius: 19px;
  box-shadow: 0 12px 35px rgba(7, 20, 71, 0.14);
  padding: 5px;
  @include flex-container(row, space-around, stretch);
}

.StudentBottomNav-item {
  @include flex-container(column, center, center);
  flex: 1;
  // Borna bug batch 1 (item 8): with a fourth cell the bar has to be able to
  // give ground. Without this a long label sets the cell's min-content width
  // and the flex items refuse to shrink, pushing the bar past the viewport at
  // 360px. V2's own `grid-template-columns: repeat(n, 1fr)` has the same
  // effect; this is the flexbox equivalent.
  min-width: 0;
  gap: 4px;
  padding: 0;
  color: $borna-muted;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  // The logout cell is a <button>; strip the UA chrome so it sits in the row
  // identically to the three router-links.
  &--logout {
    background: none;
    border: 0;
    font: inherit;
    cursor: pointer;
    color: $borna-danger;

    .StudentBottomNav-label {
      // V2 shrinks this one label so the longest word in the bar never wraps
      // or overflows its cell (`.bottom-nav .nav-item--logout span`).
      font-size: 8px;
      white-space: nowrap;
    }
  }

  &--active {
    color: $borna-indigo-900;

    .StudentBottomNav-icon path {
      fill: $borna-indigo-900;
    }
  }
}

.StudentBottomNav-icon {
  width: 20px;
  height: 20px;
}

.StudentBottomNav-label {
  font-size: $borna-fs-marker;
  font-weight: $borna-fw-bold;
  line-height: 1.4;
}
</style>
